import { useSectionAccess } from "../../../auth/useSectionAccess";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  materialsApi,
  type MaterialCategory,
  type MaterialKind,
  type MaterialView,
} from "../../../api/materials";
import type { ApiError } from "../../../api/client";
import { useConfirm } from "../../../components/ConfirmDialog";
import { Select } from "../../../components/Select";
import { useToast } from "../../../components/Toast";
import { ALL_ROLES } from "../../../auth/roles";

const KINDS: MaterialKind[] = ["LOGO", "BROCHURE", "IMAGE", "VIDEO", "OTHER"];

export default function AdminMaterialsPage() {
  const { canWrite, canFull } = useSectionAccess("materials");
  const { t } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const [categories, setCategories] = useState<MaterialCategory[]>([]);
  const [items, setItems] = useState<MaterialView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showCategory, setShowCategory] = useState(false);

  async function load() {
    setError(null);
    try {
      setCategories(await materialsApi.categories("en"));
      setItems(await materialsApi.list());
    } catch (err) {
      setError((err as ApiError).message);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleDelete(m: MaterialView) {
    if (!(await confirm({ message: t("admin.materials.confirmDelete", { filename: m.filename }), danger: true }))) return;
    try {
      await materialsApi.delete(m.id);
      await load();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("admin.materials.title")}</h1>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" disabled={!canWrite} onClick={() => setShowCategory(true)} style={secondary}>{t("admin.materials.action.addCategory")}</button>
          <button type="button" onClick={() => setShowUpload(true)} style={primary} disabled={!canWrite || categories.length === 0}>
            {t("admin.materials.action.uploadMaterial")}
          </button>
        </div>
      </header>

      {canWrite && categories.length === 0 && (
        <div style={banner}>{t("admin.materials.banner.needCategory")}</div>
      )}

      {error && <div style={errorBox}>{error}</div>}

      {items.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.materials.empty")}</p>
      ) : (
        <table style={tbl}>
          <thead>
            <tr>
              <th style={th}>{t("admin.materials.col.filename")}</th>
              <th style={th}>{t("admin.materials.col.kind")}</th>
              <th style={th}>{t("admin.materials.col.category")}</th>
              <th style={th}>{t("admin.materials.col.visibleTo")}</th>
              <th style={th}>{t("admin.materials.col.size")}</th>
              <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.id}>
                <td style={td}>{m.filename}</td>
                <td style={td}>{m.kind}</td>
                <td style={td}>{m.categorySlug}</td>
                <td style={td}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {m.visibleToRoles.map((r) => <span key={r} style={chip}>{r}</span>)}
                  </div>
                </td>
                <td style={td}>{t("admin.materials.sizeKb", { kb: (m.sizeBytes / 1024).toFixed(0) })}</td>
                <td style={td}>
                  <button type="button" disabled={!canFull} onClick={() => handleDelete(m)} style={dangerLink}>{t("action.delete")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showCategory && canWrite && (
        <CategoryModal onClose={() => setShowCategory(false)} onCreated={async () => { setShowCategory(false); await load(); }} />
      )}
      {showUpload && canWrite && (
        <UploadModal
          categories={categories}
          onClose={() => setShowUpload(false)}
          onUploaded={async () => { setShowUpload(false); await load(); }}
        />
      )}
    </div>
  );
}

function CategoryModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => Promise<void> }) {
  const { t } = useTranslation();
  const [slug, setSlug] = useState("");
  const [enName, setEnName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await materialsApi.createCategory({ slug, translations: { en: enName } });
      await onCreated();
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-ink)" }}>{t("admin.materials.modal.newCategory")}</h2>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label={t("admin.materials.field.slug")} required>
          <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} pattern="^[a-z0-9-]{2,80}$" required style={input} />
        </Field>
        <Field label={t("admin.materials.field.englishName")} required>
          <input value={enName} onChange={(e) => setEnName(e.target.value)} required style={input} />
        </Field>
        {error && <div style={errorBox}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} style={secondary}>{t("action.cancel")}</button>
          <button type="submit" disabled={submitting} style={primary}>{submitting ? t("admin.materials.action.saving") : t("action.save")}</button>
        </div>
      </form>
    </Modal>
  );
}

function UploadModal({
  categories, onClose, onUploaded,
}: {
  categories: MaterialCategory[];
  onClose: () => void;
  onUploaded: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [categoryId, setCategoryId] = useState(categories[0]?.id ?? "");
  const [kind, setKind] = useState<MaterialKind>("BROCHURE");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [visibleRoles, setVisibleRoles] = useState<string[]>([...ALL_ROLES]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleRole(r: string) {
    setVisibleRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError(t("admin.materials.error.pickFile"));
      return;
    }
    setSubmitting(true);
    try {
      await materialsApi.upload(
        {
          categoryId,
          kind,
          visibleToRoles: visibleRoles,
          translations: { en: { title: title || file.name, description: description || null } },
        },
        file,
      );
      await onUploaded();
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-ink)" }}>{t("admin.materials.modal.uploadMaterial")}</h2>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label={t("admin.materials.field.file")} required>
          <input
            type="file"
            onChange={(e: ChangeEvent<HTMLInputElement>) => setFile(e.target.files?.[0] ?? null)}
            required
          />
        </Field>
        <div style={{ display: "flex", gap: 12 }}>
          <Field label={t("admin.materials.field.category")} required style={{ flex: 1 }}>
            <Select<string>
              value={categoryId}
              onChange={setCategoryId}
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Field>
          <Field label={t("admin.materials.field.kind")} required style={{ flex: 1 }}>
            <Select<MaterialKind>
              value={kind}
              onChange={setKind}
              options={KINDS.map((k) => ({ value: k, label: t(`materials.kind.${k}`, k) }))}
            />
          </Field>
        </div>
        <Field label={t("admin.materials.field.titleEnglish")}>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("admin.materials.field.titlePlaceholder")} style={input} />
        </Field>
        <Field label={t("admin.materials.field.descriptionEnglish")}>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} style={{ ...input, resize: "vertical", fontFamily: "inherit" }} />
        </Field>
        <Field label={t("admin.materials.field.visibleToRoles")}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {ALL_ROLES.map((r) => (
              <label key={r} style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <input type="checkbox" checked={visibleRoles.includes(r)} onChange={() => toggleRole(r)} />
                {r}
              </label>
            ))}
          </div>
        </Field>
        {error && <div style={errorBox}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} style={secondary}>{t("action.cancel")}</button>
          <button type="submit" disabled={submitting} style={primary}>{submitting ? t("admin.materials.action.uploading") : t("admin.materials.action.upload")}</button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>{children}</div>
    </div>
  );
}

function Field({ label, required, children, style }: { label: string; required?: boolean; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, ...style }}>
      <span style={{ color: "var(--ink-3)" }}>
        {label} {required && <span style={{ color: "var(--err)" }}>*</span>}
      </span>
      {children}
    </label>
  );
}

const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13, background: "var(--surface)", borderRadius: 8, overflow: "hidden", boxShadow: "var(--shadow-1)" };
const th: React.CSSProperties = { textAlign: "left", padding: "10px 12px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)" };
const chip: React.CSSProperties = { background: "var(--tone-indigo-bg)", color: "var(--tone-indigo)", fontSize: 11, padding: "2px 6px", borderRadius: 4 };
const input: React.CSSProperties = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 14, width: "100%" };
const primary: React.CSSProperties = { padding: "8px 14px", background: "var(--brand-primary-600)", color: "var(--on-primary)", border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const secondary: React.CSSProperties = { padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const dangerLink: React.CSSProperties = { background: "transparent", border: "none", color: "var(--err)", cursor: "pointer", fontSize: 13 };
const errorBox: React.CSSProperties = { padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const banner: React.CSSProperties = { padding: 10, background: "var(--warn-bg)", color: "var(--warn)", borderRadius: 6, fontSize: 13 };
const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 };
const dialog: React.CSSProperties = { width: "100%", maxWidth: 560, background: "var(--surface)", padding: 24, borderRadius: 12, boxShadow: "var(--shadow-2)", display: "flex", flexDirection: "column", gap: 14, maxHeight: "90vh", overflowY: "auto" };
